/**
 * The densest thing in the system: an avatar, two lines of text, one piece of metadata.
 *
 * Rows are separated by a hairline, not by gaps, and the first row never draws a top border.
 * Selection and press both tint with --accent rather than changing the surface, so the row still
 * reads as part of the list.
 */
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Text } from '@/components/ui/text';
import { cn } from '@/lib/utils';
import * as React from 'react';
import { Pressable, View } from 'react-native';

export function List({ className, children }: { className?: string; children: React.ReactNode }) {
  return <View className={cn('bg-card border-border overflow-hidden rounded-lg border', className)}>{children}</View>;
}

export function Row({
  monogram,
  title,
  subtitle,
  meta,
  first,
  onPress,
  selected,
  right,
}: {
  monogram?: string;
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  meta?: React.ReactNode;
  first?: boolean;
  onPress?: () => void;
  selected?: boolean;
  right?: React.ReactNode;
}) {
  return (
    <Pressable
      accessibilityRole={onPress ? 'button' : undefined}
      accessibilityState={selected === undefined ? undefined : { selected }}
      onPress={onPress}
      disabled={!onPress}
      className={cn(
        'flex-row flex-wrap items-center gap-3 px-4 py-3',
        !first && 'border-border border-t',
        onPress && 'hover:bg-accent active:bg-accent',
        selected && 'bg-accent',
      )}>
      {monogram ? (
        <Avatar alt={typeof title === 'string' ? title : monogram}>
          <AvatarFallback>
            <Text variant="small" className="text-primary-foreground font-display font-semibold">
              {monogram}
            </Text>
          </AvatarFallback>
        </Avatar>
      ) : null}

      {/**
       * A basis rather than `flex-1`, which would set it to zero: on a phone the meta and the row's
       * actions have to wrap onto their own line instead of squeezing the text to one word per
       * line. It still grows to take the whole row when there is width to take.
       */}
      <View className="min-w-0 grow basis-[200px] gap-0.5">
        {typeof title === 'string' ? <Text className="font-medium">{title}</Text> : title}
        {typeof subtitle === 'string' ? (
          <Text variant="small" numberOfLines={2} className="text-muted-foreground">
            {subtitle}
          </Text>
        ) : (
          subtitle
        )}
      </View>

      {typeof meta === 'string' ? (
        <Text variant="small" className="text-muted-foreground">
          {meta}
        </Text>
      ) : (
        meta
      )}
      {right}
    </Pressable>
  );
}
